import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Rocket, ChevronDown, Shield, FileText, Cookie, AlertTriangle,
  Mail, LifeBuoy, HelpCircle,
} from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { AdminAccessModal } from "./AdminAccessModal";
import { ContactModal } from "./ContactModal";
import { useAuth } from "../hooks/useAuth";

const SUPPORT = [
  { label: "Contact", icon: Mail, action: "contact" },
  { label: "Help Center", icon: LifeBuoy, to: "/help" },
  { label: "FAQ", icon: HelpCircle, to: "/faq" },
];
const LEGAL = [
  { label: "Privacy Policy", icon: Shield, to: "/legal/privacy" },
  { label: "Terms of Service", icon: FileText, to: "/legal/terms" },
  { label: "Cookie Policy", icon: Cookie, to: "/legal/cookies" },
  { label: "Disclaimer", icon: AlertTriangle, to: "/legal/disclaimer" },
];

export const Footer = () => {
  const [adminOpen, setAdminOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleItem = (item) => {
    setMenuOpen(false);
    if (item.action === "contact") setContactOpen(true);
    else if (item.to) navigate(item.to);
  };

  const Group = ({ title, items }) => (
    <div className="py-1.5">
      <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#9aa1ab]">{title}</p>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.label}
            onClick={() => handleItem(item)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#FBF7EA] hover:text-[#0F0F0F] transition-colors"
            data-testid={`footer-menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon size={15} className="text-[#B8901F]" /> {item.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <footer className="bg-[#0F0F0F] text-white mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          {/* Brand + copyright */}
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="LaunchBS" loading="lazy" decoding="async" className="h-9 w-9 object-contain" />
            <div>
              <p className="text-sm text-[#A1A1AA]">© 2026 <span className="font-display font-extrabold text-white">LAUNCH<span className="text-[#D4AF37]">BS</span></span>. All rights reserved.</p>
              <p className="text-sm text-[#D4AF37] font-medium mt-0.5" data-testid="footer-tagline">Discover Opportunities. Inspire Innovation. Build the Future.</p>
            </div>
          </div>

          {/* Legal & Support dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              data-testid="footer-legal-support-btn"
              aria-expanded={menuOpen}
            >
              Legal & Support
              <ChevronDown size={16} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
            {menuOpen && (
              <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-2xl border border-gray-100 shadow-2xl overflow-hidden divide-y divide-gray-100 z-30" data-testid="footer-legal-support-menu">
                <Group title="Support" items={SUPPORT} />
                <Group title="Legal" items={LEGAL} />
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <button
              onClick={() => setAdminOpen(true)}
              aria-label="Admin access"
              title="Admin access"
              className="w-10 h-10 rounded-full bg-white/5 text-[#D4AF37] border border-[#D4AF37]/50 flex items-center justify-center hover:bg-[#D4AF37]/10 transition-colors duration-300"
              data-testid="footer-admin-rocket"
            >
              <Rocket size={17} />
            </button>
          </div>
        )}
      </div>

      {isAdmin && <AdminAccessModal open={adminOpen} onClose={() => setAdminOpen(false)} />}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </footer>
  );
};
